import { useState } from "react";
import { ChevronDown, ChevronRight, ArrowRight, Target, Clock, TrendingUp, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { AllGamesQuickView } from "./AllGamesQuickView";

const FAQS = [
    {
        q: "What is Shillong Morning Teer?",
        a: "Shillong Morning Teer is the earliest daily session of the traditional archery-based number game played in Meghalaya, India. Archers of an accredited club shoot a fixed number of arrows at a bamboo target in two rounds — the First Round (FR) at around 10:30 AM and the Second Round (SR) at around 11:30 AM. The last two digits of the total arrows striking the target become the winning number for that round.",
    },
    {
        q: "What time are the Shillong Morning Teer FR & SR results declared?",
        a: "The First Round (FR) result is usually declared at 10:30 AM (IST) and the Second Round (SR) result at 11:30 AM (IST). We publish both numbers on our homepage within seconds of the official announcement, along with the exact update timestamp.",
    },
    {
        q: "How can I check today's Shillong Morning Teer result?",
        a: "Visit the homepage of Shillong Morning Teer Result Today. The featured card at the top shows today's FR and SR numbers. If the result is not yet declared, you will see 'XX' as a placeholder — the number will replace it automatically once the official announcement is made.",
    },
    {
        q: "Where can I find the Shillong Morning Teer previous results and chart?",
        a: "Open the ‘Previous Results’ page from the main menu. You can filter by date and by market (Shillong Morning, Khanapara, Juwai, Shillong Night, Bhutan, Night Teer, etc.) to see the historical result chart at a glance.",
    },
    {
        q: "What is a Common Number in Teer?",
        a: "A Common Number is a shortlist of numbers that experienced players and analysts consider more likely to appear in a given round, based on the pattern of recent results. Our platform publishes today's common numbers for every market — they are provided purely for analysis and are never a guarantee of any outcome.",
    },
    {
        q: "Is this website official?",
        a: "This is an independent information portal. We are not affiliated with any archery club or state authority. All results are published as soon as they are officially announced at the respective grounds.",
    },
    {
        q: "Is playing Teer legal in India?",
        a: "Teer is a legally regulated game of skill in the state of Meghalaya. Rules vary from state to state — you must verify the applicable laws in your own jurisdiction before participating in any way. This website is strictly informational.",
    },
];

const CHART = [
    { day: "Monday",    fr: "27", sr: "49" },
    { day: "Tuesday",   fr: "13", sr: "94" },
    { day: "Wednesday", fr: "29", sr: "20" },
    { day: "Thursday",  fr: "34", sr: "69" },
    { day: "Friday",    fr: "67", sr: "82" },
    { day: "Saturday",  fr: "48", sr: "56" },
    { day: "Sunday",    fr: "18", sr: "31" },
];

const Faq = ({ q, a, i }) => {
    const [open, setOpen] = useState(i === 0);
    return (
        <div className="card-crisp overflow-hidden" data-testid={`faq-${i}`}>
            <button
                onClick={() => setOpen(v => !v)}
                className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
                aria-expanded={open}
            >
                <span className="font-display font-bold text-blue-950 tracking-tight text-base md:text-lg">{q}</span>
                <ChevronDown className={`h-5 w-5 text-blue-900 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="px-5 pb-5 pt-0 text-slate-600 text-[15px] leading-relaxed border-t border-slate-100">
                    {a}
                </div>
            )}
        </div>
    );
};

export const HomeSeoContent = () => {
    return (
        <>
            {/* Replaced the 3 static FR/SR/Common cards with a richer per-game quick view */}
            <AllGamesQuickView />

            {/* Latest Morning Teer Results — heading (H2) for SEO */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-16" data-testid="section-latest-results">
                <h2 className="font-display font-bold text-3xl md:text-4xl text-blue-950 tracking-tight">
                    Latest Morning Teer Results
                </h2>
                <p className="mt-3 text-slate-600 max-w-3xl leading-relaxed">
                    Our homepage always displays the latest Shillong Morning Teer FR and SR numbers as
                    soon as they are declared at the ground. If you are a regular reader, bookmark this
                    page — we ensure results reach you within seconds of the official announcement so
                    that you never have to refresh multiple websites. Each of the seven markets covered
                    on the platform (Shillong Morning, Shillong Teer, Khanapara Teer, Juwai Teer,
                    Shillong Night, Night Teer and Bhutan Teer) is presented in its own colour-coded
                    card so you can spot the market you want at a glance.
                </p>
            </section>

            {/* Previous Results H2 */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-14" data-testid="section-previous-results">
                <h2 className="font-display font-bold text-3xl md:text-4xl text-blue-950 tracking-tight">
                    Previous Results
                </h2>
                <p className="mt-3 text-slate-600 max-w-3xl leading-relaxed">
                    Serious players know that the trend of previous outcomes is one of the strongest
                    tools for analysing the next round. Our <Link to="/previous-results" className="text-blue-900 underline hover:text-blue-700">Previous Results archive</Link>{" "}
                    lets you filter by exact date and by market. Every number that was ever posted on
                    the site stays permanently available — you can pull up last week, last month or
                    even last year in a single click. This is especially useful when you want to spot
                    repeating pairs, consecutive digits, or dry-run streaks in a specific market like
                    Khanapara or Juwai.
                </p>
                <div className="mt-6">
                    <Link
                        to="/previous-results"
                        data-testid="seo-prev-cta"
                        className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold"
                    >
                        View Full Archive <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </section>

            {/* Morning Teer Result Chart H2 */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-14" data-testid="section-result-chart">
                <h2 className="font-display font-bold text-3xl md:text-4xl text-blue-950 tracking-tight">
                    Morning Teer Result Chart
                </h2>
                <p className="mt-3 text-slate-600 max-w-3xl leading-relaxed">
                    The Shillong Morning Teer Result Chart below is an illustrative example that shows
                    how the daily FR and SR numbers are recorded across a full week. The real,
                    live-updating chart is available on the <Link to="/previous-results" className="text-blue-900 underline hover:text-blue-700">Previous Results</Link>{" "}
                    page — it is fully searchable and automatically refreshes as new results come in.
                </p>
                <div className="mt-6 card-crisp overflow-hidden">
                    <div className="grid grid-cols-12 bg-blue-900 text-white text-xs uppercase tracking-widest font-semibold px-4 py-3">
                        <div className="col-span-6">Day</div>
                        <div className="col-span-3 text-right">FR</div>
                        <div className="col-span-3 text-right">SR</div>
                    </div>
                    {CHART.map((r) => (
                        <div key={r.day} className="grid grid-cols-12 items-center px-4 py-3 border-t border-slate-100 hover:bg-slate-50 transition-colors">
                            <div className="col-span-6 text-sm text-slate-700 font-semibold">{r.day}</div>
                            <div className="col-span-3 text-right result-digit text-lg text-blue-900">{r.fr}</div>
                            <div className="col-span-3 text-right result-digit text-lg text-blue-900">{r.sr}</div>
                        </div>
                    ))}
                    <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500 flex items-center gap-2">
                        <Award className="h-3.5 w-3.5" /> Illustrative sample chart · real data lives at /previous-results
                    </div>
                </div>
            </section>

            {/* About Shillong Morning Teer H2 */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-14" data-testid="section-about-teer">
                <h2 className="font-display font-bold text-3xl md:text-4xl text-blue-950 tracking-tight">
                    About Shillong Morning Teer
                </h2>
                <div className="mt-3 max-w-4xl text-slate-600 leading-relaxed space-y-4 text-[15px] md:text-base">
                    <p>
                        Shillong Morning Teer is a traditional archery-based number game held every day
                        (except Sunday, at some clubs) at Polo Ground in Shillong, the capital of
                        Meghalaya. Unlike modern lotteries, the winning number in Teer is generated by
                        <strong> real archers</strong> shooting <strong>arrows</strong> at a straw target
                        in front of a live audience — it is transparency and tradition rolled into one.
                    </p>
                    <p>
                        The Morning session is the first draw of the day. Fifty (approximately) trained
                        archers shoot thirty arrows each in the <strong>First Round (FR)</strong>. The
                        counting officials tally the total number of arrows that pierced the target and
                        the last two digits become the FR winning number. About an hour later the
                        archers return for the <strong>Second Round (SR)</strong> and a fresh count
                        produces the SR winning number.
                    </p>
                    <p>
                        Because the entire process is public, the results are impossible to manipulate.
                        This is why fans across Meghalaya, Assam and West Bengal follow Shillong Morning
                        Teer with such intensity. Websites like ours exist to make these publicly
                        declared numbers instantly accessible on your phone — no need to visit the
                        ground physically or wait for a WhatsApp forward.
                    </p>
                    <p>
                        On <strong>Shillong Morning Teer Result Today</strong> you'll also find results
                        for markets such as <strong>Khanapara Teer</strong> (Guwahati, Assam),
                        <strong> Juwai Teer</strong>, <strong>Shillong Teer</strong>, the new{" "}
                        <strong>Lucky Night Teer</strong> and the late-night <strong>Night Teer</strong> — a
                        one-stop dashboard for the entire ecosystem.
                    </p>
                    <p>
                        In addition to raw results we publish curated <strong>Common Numbers</strong>,{" "}
                        <strong>Dream Numbers</strong> (interpretations of dreams into numerical guesses)
                        and <strong>Direct Hit Guti</strong> — a shortlist of the numbers our analysts
                        rate most likely for the day. These predictions are compiled purely for
                        educational and analytical value and should never be treated as guarantees.
                    </p>
                    <p>
                        The site is built <strong>mobile-first</strong>: pages weigh only a few
                        kilobytes, load instantly on 3G, and re-render results automatically every 30
                        seconds. All content is written in clear, easy-to-read English so anyone —
                        whether you are new to Teer or a long-time follower — can navigate confidently.
                    </p>
                </div>
            </section>

            {/* FAQ H2 */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-14 mb-6" data-testid="section-faq">
                <h2 className="font-display font-bold text-3xl md:text-4xl text-blue-950 tracking-tight">
                    Frequently Asked Questions
                </h2>
                <p className="mt-3 text-slate-600 max-w-3xl">
                    Everything you need to know about Shillong Morning Teer, our website and how we
                    publish results — answered in plain English.
                </p>

                <div className="mt-6 space-y-3">
                    {FAQS.map((f, i) => (
                        <Faq key={i} q={f.q} a={f.a} i={i} />
                    ))}
                </div>

                {/* FAQ Schema */}
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "FAQPage",
                            mainEntity: FAQS.map(f => ({
                                "@type": "Question",
                                name: f.q,
                                acceptedAnswer: { "@type": "Answer", text: f.a },
                            })),
                        }),
                    }}
                />
            </section>
        </>
    );
};
